import React, { useState } from "react";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Snackbar,
  Alert,
  Autocomplete
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import SaveIcon from "@mui/icons-material/Save";
import {createBankerRegistration, getBankerRegistration, getBanks, createBank} from "../../../services/banker.service";
import {commonMessage} from "../../../constants/message.constant";

interface BankerFormValues {
  associatedBank: string;
  workEmail: string;
  preferredDealTypes: string;
  maxLTV: string;
  minDSCRatio: string;
  requiredCollateralCoverage: string;
}

interface Bank {
  id: string;
  name: string;
}

const BankerRegister: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [newBankName, setNewBankName] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "success" });

  const formik = useFormik<BankerFormValues>({
    initialValues: {
      associatedBank: "",
      workEmail: "",
      preferredDealTypes: "",
      maxLTV: "",
      minDSCRatio: "",
      requiredCollateralCoverage: "",
    },
    validationSchema: Yup.object({
      associatedBank: Yup.string().required("Associated bank is required"),
      workEmail: Yup.string()
        .email("Invalid email address")
        .required("Work email is required"),
      preferredDealTypes: Yup.string(),
      maxLTV: Yup.number()
        .typeError("Must be a number")
        .required("Max LTV is required")
        .min(0, "Must be at least 0")
        .max(100, "Cannot exceed 100"),
      minDSCRatio: Yup.number()
        .typeError("Must be a number")
        .required("Min DSCR Ratio is required")
        .min(0, "Must be at least 0"),
      requiredCollateralCoverage: Yup.number()
        .typeError("Must be a number")
        .required("Required Collateral Coverage is required")
        .min(0, "Must be at least 0"),
    }),
    onSubmit: async (values) => {
      if (values.associatedBank === "other") {
        if (!newBankName.trim()) {
          setSnackbar({
            open: true,
            message: "New bank name is required",
            type: "error",
          });
          return;
        }
        try {
          const createdBank = await createBank(newBankName.trim());
          values.associatedBank = createdBank.name;
          setBanks(prevBanks => [...prevBanks, createdBank]);
          setNewBankName("");
        } catch (error: any) {
          setSnackbar({
            open: true,
            message: error?.message || "Failed to create bank",
            type: "error",
          });
          return;
        }
      }

      setLoading(true);
      try {
        const res = await createBankerRegistration(values);
        setSnackbar({
          open: true,
          message: "Registration saved!",
          type: "success",
        });
        // Optionally update form values with response data
        if (res.data?.data) {
          formik.setValues(res.data.data);
        }
      } catch (error: any) {
        setSnackbar({
          open: true,
          message: error?.message || error?.response?.data?.message || commonMessage.unknownError,
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    },
  });

  React.useEffect(() => {
    const fetchBanks = async () => {
      try {
        const bankList = await getBanks();
        setBanks(bankList);
      } catch (error) {
        console.error("Error fetching banks:", error);
      }
    };

    fetchBanks();
  }, []);

  React.useEffect(() => {
    getBankerRegistration()
      .then(res => {
        if (res) {
          formik.setValues(res);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasDealTypeErrors = formik.touched.preferredDealTypes && Boolean(formik.errors.preferredDealTypes);
  return (
    <>
      <Box sx={{ mx: "auto" }}>
        <Card elevation={3}>
          <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
            <Typography variant="h5" className="mb-6 font-bold text-gray-800">
              Register Details
            </Typography>

            <Box
              component="form"
              onSubmit={formik.handleSubmit}
              noValidate
              className="mt-4"
            >
              <Grid container spacing={3}>
                {/* Associated Bank - Full Width */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    fullWidth
                    options={[...banks.map((bank) => bank.name), "Other"]}
                    value={formik.values.associatedBank}
                    onChange={(event, newValue) => {
                      if (newValue === "Other") {
                        formik.setFieldValue("associatedBank", "other");
                      } else {
                        formik.setFieldValue("associatedBank", newValue || "");
                      }
                    }}
                    onBlur={formik.handleBlur}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Associated lender *"
                        name="associatedBank"
                        error={
                          formik.touched.associatedBank &&
                          Boolean(formik.errors.associatedBank)
                        }
                        helperText={
                          formik.touched.associatedBank &&
                          formik.errors.associatedBank
                        }
                        placeholder="Select or search associated bank"
                        InputProps={{
                          ...params.InputProps,
                          style: { borderRadius: 10 },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        {option}
                      </li>
                    )}
                  />
                  {formik.values.associatedBank === "other" && (
                    <TextField
                      fullWidth
                      label="New Bank Name *"
                      value={newBankName}
                      onChange={(e) => setNewBankName(e.target.value)}
                      placeholder="Enter new bank name"
                      InputProps={{ style: { borderRadius: 10 } }}
                      sx={{ mt: 2 }}
                    />
                  )}
                </Grid>

                {/* Work Email */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Work Email *"
                    name="workEmail"
                    value={formik.values.workEmail}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.workEmail &&
                      Boolean(formik.errors.workEmail)
                    }
                    helperText={
                      formik.touched.workEmail && formik.errors.workEmail
                    }
                    placeholder="example@company.com"
                    InputProps={{ style: { borderRadius: 10 } }}
                  />
                </Grid>

                {/* Preferred Deal Types */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl
                    fullWidth
                    size="medium"
                    error={hasDealTypeErrors}
                  >
                    <InputLabel id="deal-type-label">
                      Preferred Deal Types *
                    </InputLabel>
                    <Select
                      labelId="deal-type-label"
                      id="preferredDealTypes" // Use the correct field name for id
                      name="preferredDealTypes" // Use the correct field name for name
                      value={formik.values.preferredDealTypes}
                      onBlur={formik.handleBlur}
                      // For Select, formik.handleChange works fine for updating the value
                      onChange={formik.handleChange}
                      label="Preferred Deal Types *"
                      className="bg-white"
                      sx={{ borderRadius: "10px", textAlign: "left" }}
                    >
                      <MenuItem value="">
                        <em>Select...</em>
                      </MenuItem>
                      <MenuItem value="purchase">Purchase</MenuItem>
                      <MenuItem value="refinance">Refinance</MenuItem>
                      <MenuItem value="startup">Startup</MenuItem>
                    </Select>
                    {hasDealTypeErrors && (
                      <FormHelperText sx={{ ml: 1.5 }}>
                        {formik.errors.preferredDealTypes}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Max LTV */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Max LTV *"
                    name="maxLTV"
                    value={formik.values.maxLTV}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.maxLTV && Boolean(formik.errors.maxLTV)
                    }
                    helperText={formik.touched.maxLTV && formik.errors.maxLTV}
                    placeholder="e.g., 80"
                    InputProps={{ style: { borderRadius: 10 } }}
                  />
                </Grid>

                {/* Min DSCR */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Min DSCR Ratio *"
                    name="minDSCRatio"
                    value={formik.values.minDSCRatio}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.minDSCRatio &&
                      Boolean(formik.errors.minDSCRatio)
                    }
                    helperText={
                      formik.touched.minDSCRatio && formik.errors.minDSCRatio
                    }
                    placeholder="e.g., 1.2"
                    InputProps={{ style: { borderRadius: 10 } }}
                  />
                </Grid>

                {/* Required Collateral Coverage */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Required Collateral Coverage *"
                    name="requiredCollateralCoverage"
                    value={formik.values.requiredCollateralCoverage}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.requiredCollateralCoverage &&
                      Boolean(formik.errors.requiredCollateralCoverage)
                    }
                    helperText={
                      formik.touched.requiredCollateralCoverage &&
                      formik.errors.requiredCollateralCoverage
                    }
                    placeholder="e.g., 120"
                    InputProps={{ style: { borderRadius: 10 } }}
                  />
                </Grid>

                {/* Buttons */}
                <Grid size={{ xs: 12, md: 12 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      sx={{ borderRadius: 2, fontWeight: 600 }}
                      startIcon={!loading ? <SaveIcon /> : null}
                    >
                      SAVE
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          elevation={6}
          severity={snackbar.type as any}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BankerRegister;
